import React, { useEffect, useMemo, useState } from "react";
import { BoundingBoxCircles, CardImage, FileEarmarkText, GeoAlt, Magic, PencilSquare } from "react-bootstrap-icons";
import { useError } from "../../contexts/ErrorContext";
import { useSuccess } from "../../contexts/SuccessContext";
import { CalibrationVideosAPI, CamerasAPI, RecordsAPI } from "../../services/api";
import {
    CalibrationVideo,
    Camera,
    CameraCalibrationAndExtrinsics,
    CameraMatrix,
    DistortionCoefficients,
    Pose,
    RecordTypeEnum,
    VideoRecord,
    WorkflowCatalog,
    WorkflowTemplateDefinition
} from "../../types/index";
import { findBestMatchingCalibration } from "../../utils/videos";
import CalibrationPanel from "./CalibrationPanel";

interface BatchWorkflowModalProps {
    show: boolean;
    onHide: () => void;
    onComplete: (records: VideoRecord[]) => void;
    records: VideoRecord[];
}

const TEMPLATE_REQUIRES_CALIBRATION = new Set(["only_ia", "ia_preannotation", "cvat_export"]);

const START_WORKFLOWS_CHUNK_SIZE = 10;

const TEMPLATE_ICONS: Record<string, React.ReactNode> = {
    gps_extraction: <GeoAlt size={38} />,
    image_extraction: <CardImage size={38} />,
    only_ia: <BoundingBoxCircles size={38} />,
    ia_preannotation: <PencilSquare size={38} />,
    cvat_export: <FileEarmarkText size={38} />,
};

const BatchWorkflowModal: React.FC<BatchWorkflowModalProps> = ({
    show,
    onHide,
    onComplete,
    records
}) => {
    const [workflowCatalog, setWorkflowCatalog] = useState<WorkflowCatalog | null>(null);
    const [selectedTemplateKey, setSelectedTemplateKey] = useState<string>("");
    const [calibrationVideos, setCalibrationVideos] = useState<CalibrationVideo[]>([]);
    const [, setCameras] = useState<Camera[]>([]);
    const [selectedCalibrationVideoId, setSelectedCalibrationVideoId] = useState<number | null>(null);
    const [calibrationData, setCalibrationData] = useState<CameraCalibrationAndExtrinsics>(
        new CameraCalibrationAndExtrinsics()
    );
    const [loading, setLoading] = useState<boolean>(true);
    const [fullReset, setFullReset] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [startedCount, setStartedCount] = useState<number | null>(null);
    const { setError } = useError();
    const { setSuccess } = useSuccess();

    useEffect(() => {
        if (!show) {
            return;
        }
        fetchWorkflowData();
        fetchCalibrationVideos();
        fetchCameras();
    }, [show]);

    const selectedRecordTypes = useMemo<Set<string>>(
        () => new Set(records.map((record) => String(record.type))),
        [records]
    );

    const selectedTemplate = useMemo<WorkflowTemplateDefinition | null>(() => {
        if (!workflowCatalog) {
            return null;
        }
        return workflowCatalog.templates.find((template) => template.key === selectedTemplateKey) || null;
    }, [workflowCatalog, selectedTemplateKey]);

    const showCalibrationPanel = useMemo(
        () => selectedTemplate !== null && TEMPLATE_REQUIRES_CALIBRATION.has(selectedTemplate.key),
        [selectedTemplate]
    );

    const templateAvailability = useMemo(() => {
        const disabledByTemplate = new Set<string>();
        if (!workflowCatalog) {
            return disabledByTemplate;
        }
        workflowCatalog.templates.forEach((template) => {
            const isDisabled = template.disabled_record_types.some((recordType) => selectedRecordTypes.has(recordType));
            if (isDisabled) {
                disabledByTemplate.add(template.key);
            }
        });
        return disabledByTemplate;
    }, [workflowCatalog, selectedRecordTypes]);

    useEffect(() => {
        if (!workflowCatalog) {
            return;
        }
        const preferredKey = workflowCatalog.default_template;
        if (preferredKey && !templateAvailability.has(preferredKey)) {
            setSelectedTemplateKey(preferredKey);
            return;
        }
        const firstAvailable = workflowCatalog.templates.find((template) => !templateAvailability.has(template.key));
        setSelectedTemplateKey(firstAvailable?.key || workflowCatalog.default_template);
    }, [workflowCatalog, templateAvailability]);

    const fetchCameras = async () => {
        try {
            const response = await CamerasAPI.getCameras();
            setCameras(response.data);
        } catch (error) {
            setError(`Failed to load calibration cameras: ${error}`);
        }
    };

    const fetchCalibrationVideos = async () => {
        try {
            const response = await CamerasAPI.getAllCalibrationVideos();
            setCalibrationVideos(response.data);
        } catch (error) {
            setError(`Failed to load calibration videos: ${error}`);
        }
    };

    useEffect(() => {
        if (calibrationVideos.length === 0) {
            return;
        }
        const bestMatch = findBestMatchingCalibrationFromFirstSelectedRecord(calibrationVideos);
        if (bestMatch) {
            setSelectedCalibrationVideoId(bestMatch.id);
        }
    }, [calibrationVideos]);

    useEffect(() => {
        if (selectedCalibrationVideoId) {
            loadAndSetCalibrationFromVideo(selectedCalibrationVideoId);
            return;
        }
        setCalibrationData(new CameraCalibrationAndExtrinsics());
    }, [selectedCalibrationVideoId]);

    const loadAndSetCalibrationFromVideo = async (videoId: number) => {
        try {
            const response = await CalibrationVideosAPI.get(videoId);
            const video = response.data;

            if (video.intrinsics && video.distortion_coeffs) {
                const calibration = new CameraCalibrationAndExtrinsics({
                    camera_matrix: new CameraMatrix({
                        fx: video.intrinsics[0],
                        fy: video.intrinsics[4],
                        cx: video.intrinsics[2],
                        cy: video.intrinsics[5],
                        skew: video.intrinsics[1]
                    }),
                    distortion_coefficients: new DistortionCoefficients({
                        k1: video.distortion_coeffs[0],
                        k2: video.distortion_coeffs[1],
                        p1: video.distortion_coeffs[2],
                        p2: video.distortion_coeffs[3],
                        k3: video.distortion_coeffs[4]
                    }),
                    pose: new Pose(),
                    image_width: video.image_width || 1920,
                    image_height: video.image_height || 1080
                });
                setCalibrationData(calibration);
            }
        } catch (error) {
            setError(`Failed to load calibration data: ${error}`);
        }
    };

    const findBestMatchingCalibrationFromFirstSelectedRecord = (videos: CalibrationVideo[]): CalibrationVideo | null => {
        if (records.length === 0 || records[0].videos.length === 0) {
            return null;
        }
        const masterVideo = records[0].videos.find((video) => video.is_master) || records[0].videos[0];
        return findBestMatchingCalibration(masterVideo, videos);
    };

    const fetchWorkflowData = async () => {
        try {
            setLoading(true);
            const response = await RecordsAPI.getWorkflowCustomData();
            //const response = await RecordsAPI.getDebugWorkflowCustomData();
            setWorkflowCatalog(response.data);
        } catch (error) {
            setError(`Failed to load workflow configuration: ${error}`);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!selectedTemplateKey) {
            setError("Select a workflow template.");
            return;
        }
        if (templateAvailability.has(selectedTemplateKey)) {
            setError("The selected workflow template is not available for the current record selection.");
            return;
        }

        const customData: { [key: string]: unknown } = {
            workflow_template: selectedTemplateKey,
        };
        if (showCalibrationPanel && selectedCalibrationVideoId) {
            customData.calibration = calibrationData.toJSON();
        }

        try {
            const recordIds = records.map((record) => record.id);
            if (fullReset) {
                setIsDeleting(true);
                try {
                    const deleteResponse = await RecordsAPI.deleteExtractedProcessedFiles(recordIds);
                    const failedDeletions = deleteResponse.data.results.filter((result) => result.status === "error");

                    if (failedDeletions.length > 0) {
                        const errorMessages = failedDeletions.map((failure) =>
                            `Record ${failure.record_name || failure.record_id}: ${failure.error || "Unknown error"}`
                        ).join("; ");
                        setError(`Some files could not be deleted: ${errorMessages}. Workflow will still start.`);
                    } else {
                        setSuccess(`Successfully deleted processed files for ${records.length} records`);
                    }
                } catch (deleteError) {
                    setError(`Failed to delete files: ${deleteError}. Aborting workflow start.`);
                    setIsDeleting(false);
                    return;
                } finally {
                    setIsDeleting(false);
                }
            }

            let started = 0;
            setStartedCount(0);
            try {
                for (let i = 0; i < records.length; i += START_WORKFLOWS_CHUNK_SIZE) {
                    const chunk = records.slice(i, i + START_WORKFLOWS_CHUNK_SIZE);
                    const items = chunk.map((record) => ({ record_id: record.id }));
                    const response = await RecordsAPI.startWorkflowsBatch(items, customData);

                    if (response.status !== 200) {
                        throw new Error(response.data?.error || "Unknown error");
                    }
                    setStartedCount((started += chunk.length));
                }
            } catch (error) {
                throw new Error(`${error} (${started}/${records.length} started)`);
            } finally {
                setStartedCount(null);
            }

            onHide();
            const message = fullReset
                ? `Files deleted and batch workflow started for ${records.length} records`
                : `Batch workflow started for ${records.length} records`;
            setSuccess(message);
            onComplete(records);
        } catch (error) {
            setError(`Failed to start batch workflow: ${error}`);
        }
    };

    const renderTemplateCard = (template: WorkflowTemplateDefinition) => {
        const isSelected = selectedTemplateKey === template.key;
        const isDisabled = templateAvailability.has(template.key);
        const icon = TEMPLATE_ICONS[template.key] || <Magic size={38} />;

        return (
            <button
                key={template.key}
                type="button"
                className={`btn text-start border rounded p-4 h-100 w-100 shadow-sm ${isSelected ? "border-primary bg-primary-subtle" : "bg-white"}`}
                onClick={() => setSelectedTemplateKey(template.key)}
                disabled={isDisabled}
                style={{ minHeight: "112px" }}
            >
                <div className="d-flex align-items-start">
                    <div className="me-3 text-primary" style={{ lineHeight: 0 }}>
                        {icon}
                    </div>
                    <div className="d-flex flex-column justify-content-center">
                        <div className="fw-semibold">{template.label}</div>
                        <div className="text-muted small">{template.description}</div>
                        {isDisabled && (
                            <div className="text-danger small mt-2">
                                Unavailable when the selection contains {RecordTypeEnum.PANORAMA} records.
                            </div>
                        )}
                    </div>
                </div>
            </button>
        );
    };

    if (!show) {
        return null;
    }

    return (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-xl">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Start Batch Workflow</h5>
                        <button type="button" className="btn-close" onClick={onHide}></button>
                    </div>
                    <div className="modal-body">
                        <div className="alert alert-info">
                            This will start the workflow for {records.length} selected records.
                        </div>

                        {startedCount !== null && (
                            <div className="progress mb-3">
                                <div
                                    className="progress-bar progress-bar-striped progress-bar-animated"
                                    style={{ width: `${(startedCount / records.length) * 100}%` }}
                                >
                                    {startedCount} / {records.length}
                                </div>
                            </div>
                        )}

                        <div className="alert alert-warning mb-3">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="fullReset"
                                    checked={fullReset}
                                    onChange={(e) => setFullReset(e.target.checked)}
                                    disabled={loading || isDeleting}
                                />
                                <label className="form-check-label" htmlFor="fullReset">
                                    <strong>Full Reset:</strong> Delete all files in extracted and processed containers before starting workflow
                                </label>
                            </div>
                            {fullReset && (
                                <small className="text-danger d-block mt-2">
                                    <i className="bi bi-exclamation-triangle-fill me-1"></i>
                                    <strong>Warning:</strong> This will permanently delete all processed data for the selected records.
                                </small>
                            )}
                        </div>

                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2">Loading workflow configuration...</p>
                            </div>
                        ) : (
                            <>
                                <h5 className="mb-3">Choose a workflow type.</h5>
                                <div className="row g-3 mb-4">
                                    {workflowCatalog?.templates.map((template, index, templates) => {
                                        const isCenteredLastCard = templates.length % 2 === 1 && index === templates.length - 1;
                                        return (
                                            <div
                                                className={isCenteredLastCard ? "col-md-6 offset-md-3" : "col-md-6"}
                                                key={template.key}
                                            >
                                                {renderTemplateCard(template)}
                                            </div>
                                        );
                                    })}
                                </div>
                                {showCalibrationPanel && (
                                    <div className="row">
                                        <CalibrationPanel
                                            calibrationVideos={calibrationVideos}
                                            selectedCalibrationVideoId={selectedCalibrationVideoId}
                                            calibrationData={calibrationData}
                                            onVideoSelect={setSelectedCalibrationVideoId}
                                            onCalibrationChange={setCalibrationData}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onHide}>
                            Close
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSubmit}
                            disabled={loading || isDeleting || startedCount !== null || !selectedTemplateKey || templateAvailability.has(selectedTemplateKey)}
                        >
                            {startedCount !== null ? "Starting..." : "Start Batch Workflow"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BatchWorkflowModal;
